// Comments API client for fetching post comments

import { Comment } from '@/types/comment'

const STEEMIT_API_URL = 'https://api.steemit.com'

export interface SteemitComment {
  post_id: number
  author: string
  permlink: string
  category: string
  title: string
  body: string
  json_metadata: string
  created: string
  last_update: string
  depth: number
  children: number
  net_rshares: number
  last_payout: string
  cashout_time: string
  total_payout_value: string
  curator_payout_value: string
  pending_payout_value: string
  author_reputation: number
  root_author: string
  root_permlink: string
  parent_author: string
  parent_permlink: string
  url: string
  active_votes: Array<{
    voter: string
    weight: number
    rshares: number
    percent: number
    reputation: number
    time: string
  }>
}

interface SteemitAPIResponse {
  jsonrpc: string
  result: SteemitComment[]
  id: number
}

/**
 * Transforms Steemit API comment to our Comment type
 */
function transformComment(raw: SteemitComment): Comment {
  // Parse payout values
  const pendingPayout = parseFloat(raw.pending_payout_value?.split(' ')[0] || '0')
  const totalPayout = parseFloat(raw.total_payout_value?.split(' ')[0] || '0')
  const curatorPayout = parseFloat(raw.curator_payout_value?.split(' ')[0] || '0')
  const payout = pendingPayout > 0 ? pendingPayout : totalPayout + curatorPayout

  // Count votes
  const upvotes = raw.active_votes?.filter(v => v.percent > 0).length || 0
  const downvotes = raw.active_votes?.filter(v => v.percent < 0).length || 0

  return {
    linkId: raw.post_id,
    author: raw.author,
    permlink: raw.permlink,
    body: raw.body,
    created: new Date(raw.created + 'Z'),
    lastUpdate: new Date(raw.last_update + 'Z'),
    cashoutTime: new Date(raw.cashout_time + 'Z'),
    parentAuthor: raw.parent_author,
    parentPermlink: raw.parent_permlink,
    rootAuthor: raw.root_author,
    rootPermlink: raw.root_permlink,
    depth: raw.depth,
    children: [], // Will be populated if we build a tree
    childCount: raw.children,
    upvoteCount: upvotes,
    downvoteCount: downvotes,
    payout,
    authorReputation: raw.author_reputation,
    isMuted: false,
    url: raw.url,
  }
}

/**
 * Fetches replies to a specific post or comment using Steemit API
 */
export async function getContentReplies(
  author: string,
  permlink: string
): Promise<Comment[]> {
  try {
    const response = await fetch(STEEMIT_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'condenser_api.get_content_replies',
        params: [author, permlink],
        id: 1,
      }),
    })

    if (!response.ok) {
      throw new Error(`API error: ${response.statusText}`)
    }

    const data: SteemitAPIResponse = await response.json()

    if (!data.result || !Array.isArray(data.result)) {
      return []
    }

    return data.result.map(transformComment)
  } catch (error) {
    console.error('Failed to fetch content replies:', error)
    throw new Error('Failed to fetch comments')
  }
}

/**
 * Recursively fetches all replies to build a comment tree
 */
export async function getCommentTree(
  author: string,
  permlink: string,
  maxDepth: number = 10
): Promise<Comment[]> {
  const topLevelComments = await getContentReplies(author, permlink)

  if (maxDepth <= 1 || topLevelComments.length === 0) {
    return topLevelComments
  }

  // Fetch nested replies for comments that have children
  const commentsWithReplies = await Promise.all(
    topLevelComments.map(async (comment) => {
      if (comment.childCount > 0) {
        try {
          const children = await getCommentTree(
            comment.author,
            comment.permlink,
            maxDepth - 1
          )
          return { ...comment, children }
        } catch {
          return comment
        }
      }
      return comment
    })
  )

  return commentsWithReplies
}
